package com.shadowings.gero.presentation.screen.characters

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.GameConstants
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.avatars.ZetaAvatar
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.GLASSES_ACCENT_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.GLASSES_KEY
import com.shadowings.gero.presentation.screen.characters.CharacterConsts.GLASSES_TYPE_KEY
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import compose.icons.FeatherIcons
import compose.icons.feathericons.Lock
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.builder_pro_unlock_btn
import gero.composeapp.generated.resources.chiudi_btn
import gero.composeapp.generated.resources.custom_glasses_locked_desc
import gero.composeapp.generated.resources.custom_glasses_locked_title
import gero.composeapp.generated.resources.custom_glasses_type_base
import gero.composeapp.generated.resources.custom_glasses_type_ciclope
import gero.composeapp.generated.resources.custom_glasses_type_gold
import gero.composeapp.generated.resources.custom_glasses_type_pixel
import gero.composeapp.generated.resources.custom_hat_locked_caption
import gero.composeapp.generated.resources.custom_title_bg
import gero.composeapp.generated.resources.custom_title_body
import gero.composeapp.generated.resources.custom_title_face
import gero.composeapp.generated.resources.custom_title_glasses
import gero.composeapp.generated.resources.custom_title_glasses_accent
import gero.composeapp.generated.resources.custom_title_glasses_type
import gero.composeapp.generated.resources.custom_title_hair
import gero.composeapp.generated.resources.custom_title_shirt
import gero.composeapp.generated.resources.cyclope
import gero.composeapp.generated.resources.gold
import gero.composeapp.generated.resources.pixel
import gero.composeapp.generated.resources.zeta_base
import org.jetbrains.compose.resources.stringResource

@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun ZetaCharacterScreenPreview() {
    AppTheme {
        Surface {
            ZetaCharacterScreen(
                userData = UserData(zetaColors = mapOf()),
                onZetaColorChanged = { _, _ -> }
            )
        }
    }
}

/** level=0 (below every glasses requirement): shows the picker with locks + captions. */
@Preview(name = "Glasses locked")
@Composable
private fun ZetaCharacterScreenGlassesLockedPreview() {
    AppTheme {
        Surface {
            ZetaCharacterScreen(
                userData = UserData(zetaColors = mapOf(), experience = 0),
                onZetaColorChanged = { _, _ -> }
            )
        }
    }
}

@Composable
fun ZetaCharacterScreen(
    userData: UserData,
    onZetaColorChanged: (categoryKey: String, index: Int) -> Unit,
    onResetClick: () -> Unit = {},
    onPurchaseFullVersion: () -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val bgColorIndex =
        (userData.zetaColors[CharacterConsts.BG_KEY] ?: 0).coerceIn(0, backgroundOptions.size - 1)
    val skinColorIndex = (userData.zetaColors["skin"] ?: 0).coerceIn(0, skinOptions.size - 1)
    val hairColorIndex = (userData.zetaColors["hair"] ?: 9).coerceIn(0, hairOptions.size - 1)
    val dressColorIndex = (userData.zetaColors["dress"] ?: 5).coerceIn(0, shirtOptions.size - 1)
    val glassesColorIndex = (userData.zetaColors[GLASSES_KEY] ?: 9).coerceIn(0, hairOptions.size - 1)
    val glassesAccentColorIndex =
        (userData.zetaColors[GLASSES_ACCENT_KEY] ?: 7).coerceIn(0, hairOptions.size - 1)
    val glassesTypeIndex = (userData.zetaColors[GLASSES_TYPE_KEY] ?: 0).coerceIn(0, 3)

    // livello minimo richiesto per ogni stile di occhiali, in ordine di opzione (0 = nessun requisito)
    val glassesTypeLevelRequirements = listOf(0, 13, 21, 25)
    val userLevel = GameConstants.getPlayerLevel(userData.experience)
    var lockedGlassesRequiredLevel by remember { mutableStateOf<Int?>(null) }

    val selectedFace = remember { mutableStateOf(Faces.Idle) }

    val deviceInfo = rememberDeviceInfo()
    val isPhoneLargePortrait =
        deviceInfo.deviceClass == DeviceClass.PHONE_LARGE && !deviceInfo.isLandscape
    val avatarSize = when (deviceInfo.deviceClass) {
        DeviceClass.PHONE_SMALL -> 64.dp
        DeviceClass.PHONE_MEDIUM -> 80.dp
        DeviceClass.PHONE_LARGE -> if (isPhoneLargePortrait) 120.dp else 110.dp
        DeviceClass.TABLET_MEDIUM -> 120.dp
        DeviceClass.TABLET_LARGE -> 150.dp
        DeviceClass.WEB_FULL -> 150.dp
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .padding(horizontal = 16.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Spacer(Modifier.size(16.dp))

        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                Modifier.background(
                    color = backgroundOptions[bgColorIndex],
                    shape = RoundedCornerShape(6.dp)
                ).padding(top = 6.dp, start = 6.dp, end = 6.dp)
            ) {
                val avatarModifier = Modifier
                    .height(avatarSize)
                    .align(Alignment.BottomCenter)
                ZetaAvatar(
                    userData = userData,
                    face = selectedFace.value,
                    modifier = avatarModifier
                )
            }

            Spacer(Modifier.width(16.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.custom_title_face),
                    style = MaterialTheme.typography.titleMedium
                )
                Spacer(Modifier.height(6.dp))
                FaceMenu(
                    selectedFace = selectedFace.value,
                    onFaceSelected = { selectedFace.value = it },
                    faces = listOf(Faces.Idle, Faces.Happy, Faces.Surprised)
                )
            }
        }

        Spacer(Modifier.size(20.dp))

        // (mainColorIndex, accentColorIndexOrNull) snapped on select, per option in order
        // base/gold/pixel/ciclope — base keeps today's default, ciclope also snaps its accent.
        val glassesTypeBaseColors = listOf(9 to null, 2 to null, 9 to null, 9 to 7)

        val glassesTypeOptionsRaw = listOf(
            Res.drawable.zeta_base to stringResource(Res.string.custom_glasses_type_base),
            Res.drawable.gold to stringResource(Res.string.custom_glasses_type_gold),
            Res.drawable.pixel to stringResource(Res.string.custom_glasses_type_pixel),
            Res.drawable.cyclope to stringResource(Res.string.custom_glasses_type_ciclope),
        )
        val glassesTypeOptions = glassesTypeOptionsRaw.mapIndexed { index, (drawable, description) ->
            val requiredLevel = glassesTypeLevelRequirements[index]
            val locked = requiredLevel > 0 && userLevel < requiredLevel && !userData.isFullVersionUnlocked
            HatTypeOptionData(
                drawable = drawable,
                contentDescription = description,
                locked = locked,
                lockCaption = if (locked) {
                    stringResource(Res.string.custom_hat_locked_caption, requiredLevel)
                } else {
                    null
                }
            )
        }

        val glassesTypeSectionContent: @Composable () -> Unit = {
            HatTypeSection(
                title = stringResource(Res.string.custom_title_glasses_type),
                options = glassesTypeOptions,
                selectedIndex = glassesTypeIndex,
                onSelect = { index ->
                    onZetaColorChanged(GLASSES_TYPE_KEY, index)
                    val (mainColor, accentColor) = glassesTypeBaseColors[index]
                    onZetaColorChanged(GLASSES_KEY, mainColor)
                    if (accentColor != null) onZetaColorChanged(GLASSES_ACCENT_KEY, accentColor)
                },
                onLockedClick = { index -> lockedGlassesRequiredLevel = glassesTypeLevelRequirements[index] }
            )
        }

        val sections = listOf(
            ColorSectionData(
                title = stringResource(Res.string.custom_title_bg),
                options = backgroundOptions,
                selectedIndex = bgColorIndex,
                onSelect = { onZetaColorChanged(CharacterConsts.BG_KEY, it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_body),
                options = skinOptions,
                selectedIndex = skinColorIndex,
                onSelect = { onZetaColorChanged("skin", it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_hair),
                options = hairOptions,
                selectedIndex = hairColorIndex,
                onSelect = { onZetaColorChanged("hair", it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_shirt),
                options = shirtOptions,
                selectedIndex = dressColorIndex,
                onSelect = { onZetaColorChanged("dress", it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_glasses),
                options = hairOptions,
                selectedIndex = glassesColorIndex,
                onSelect = { onZetaColorChanged(GLASSES_KEY, it) }
            ),
            ColorSectionData(
                title = stringResource(Res.string.custom_title_glasses_accent),
                options = hairOptions,
                selectedIndex = glassesAccentColorIndex,
                onSelect = { onZetaColorChanged(GLASSES_ACCENT_KEY, it) }
            )
        )
        ColorsPicker(
            sections = sections,
            onResetClick = onResetClick,
            headerContent = glassesTypeSectionContent
        )
    }

    val requiredLevel = lockedGlassesRequiredLevel
    if (requiredLevel != null) {
        AlertDialog(
            onDismissRequest = { lockedGlassesRequiredLevel = null },
            icon = {
                Icon(
                    imageVector = FeatherIcons.Lock,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(28.dp)
                )
            },
            title = {
                Text(
                    text = stringResource(Res.string.custom_glasses_locked_title),
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center
                )
            },
            text = {
                Text(
                    text = stringResource(Res.string.custom_glasses_locked_desc, requiredLevel),
                    textAlign = TextAlign.Center
                )
            },
            confirmButton = {
                Button(
                    onClick = {
                        lockedGlassesRequiredLevel = null
                        onPurchaseFullVersion()
                    }
                ) {
                    Text(stringResource(Res.string.builder_pro_unlock_btn))
                }
            },
            dismissButton = {
                TextButton(onClick = { lockedGlassesRequiredLevel = null }) {
                    Text(stringResource(Res.string.chiudi_btn))
                }
            }
        )
    }
}
